import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import { CURRENCIES } from '../constants/currencies';
import { formatCurrency } from '../utils/formatters';

export default function QuickSetupPage({ navigateTo, setParticipants, initialData, currency, onCurrencyChange }) {
  const [participantCount, setParticipantCount] = useState(initialData?.participantCount?.toString() || '');
  const [averageSalary, setAverageSalary] = useState(initialData?.averageSalary?.toString() || '');
  const [salaryType, setSalaryType] = useState(initialData?.salaryType || 'annual');

  const calculateCostPerMinute = () => {
    if (!participantCount || !averageSalary) return 0;
    
    const count = parseInt(participantCount);
    const salary = parseFloat(averageSalary);
    
    // Calculate hourly rate
    const hourlyRate = salaryType === 'hourly' ? salary : salary / 2080;
    
    // Total cost per minute for all participants
    return (hourlyRate * count) / 60;
  };

  const startMeeting = () => {
    const count = parseInt(participantCount);
    const salary = parseFloat(averageSalary);

    if (!count || count < 1 || !salary || salary <= 0) {
      alert('Please enter valid participant count and salary');
      return;
    }

    // Create participant objects with average salary
    const participants = [];
    for (let i = 0; i < count; i++) {
      participants.push({
        id: Date.now() + i,
        name: `Participant ${i + 1}`,
        salaryType: salaryType,
        salaryValue: salary.toString(),
        annualValue: salaryType === 'annual' ? salary.toString() : '',
        hourlyValue: salaryType === 'hourly' ? salary.toString() : '',
        isQuickMode: true // Flag to identify quick mode participants
      });
    }

    setParticipants(participants);
    navigateTo('tracker');
  };

  const costPerMinute = calculateCostPerMinute();

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quick Calculate</h1>
            <p className="text-gray-600">Fast estimate with average salary</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-900">
            <strong>💡 Quick Mode:</strong> Enter an average salary and participant count for a fast estimate. 
            Need more precision? Use <button onClick={() => navigateTo('setup')} className="underline font-semibold hover:text-blue-700">Detailed Mode</button> for individual salaries.
          </p>
        </div>

        {/* Currency Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
          <select
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            className="w-full px-4 py-3 bg-white border-2 border-purple-300 rounded-lg shadow-sm text-base font-medium focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            {Object.entries(CURRENCIES).map(([code, info]) => (
              <option key={code} value={code}>
                {info.symbol} {code} - {info.name}
              </option>
            ))}
          </select>
        </div>

        {/* Number of Participants */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Participants
          </label>
          <input
            type="number"
            value={participantCount}
            onChange={(e) => setParticipantCount(e.target.value)}
            placeholder="e.g., 5"
            min="1"
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
          />
          <p className="text-xs text-gray-500 mt-1">Enter any number (no limit)</p>
        </div>

        {/* Salary Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Average Salary Type</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSalaryType('annual')}
              className={`px-4 py-3 rounded-lg font-medium border-2 transition-all ${
                salaryType === 'annual'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              Annual Salary
            </button>
            <button
              onClick={() => setSalaryType('hourly')}
              className={`px-4 py-3 rounded-lg font-medium border-2 transition-all ${
                salaryType === 'hourly'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              Hourly Rate
            </button>
          </div>
        </div>

        {/* Average Salary Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Average {salaryType === 'annual' ? 'Annual Salary' : 'Hourly Rate'}
          </label>
          <input
            type="number"
            value={averageSalary}
            onChange={(e) => setAverageSalary(e.target.value)}
            placeholder={salaryType === 'annual' ? 'e.g., 80000' : 'e.g., 45'}
            min="0"
            step={salaryType === 'annual' ? '1000' : '1'}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
          />
          <p className="text-xs text-gray-500 mt-1">
            {salaryType === 'annual' 
              ? 'Average yearly salary across all participants' 
              : 'Average hourly rate across all participants'}
          </p>
        </div>

        {/* Estimated Cost Preview */}
        {participantCount && averageSalary && (
          <div className="mb-6 p-6 bg-purple-50 rounded-lg border-2 border-purple-200">
            <p className="text-sm text-gray-600 mb-1">Estimated Cost:</p>
            <p className="text-3xl font-bold text-purple-600">
              {formatCurrency(costPerMinute, currency)}/minute
            </p>
            <p className="text-sm text-gray-600 mt-2">
              {participantCount} participant{parseInt(participantCount) !== 1 ? 's' : ''} × 
              {salaryType === 'annual' 
                ? ` ${CURRENCIES[currency].symbol}${parseFloat(averageSalary).toLocaleString()}/year`
                : ` ${CURRENCIES[currency].symbol}${averageSalary}/hour`}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigateTo('home')}
            className="px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 font-semibold"
          >
            ← Back
          </button>
          <button
            onClick={startMeeting}
            disabled={!participantCount || !averageSalary}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500 transition-all"
          >
            Start Meeting
          </button>
        </div>
      </div>
    </div>
  );
}